import React, { useEffect, useMemo, useState } from 'react';
import { formatCalendarDate, getViewerTimezone } from '../../utils/dateTimeUtils';
import { AGE_GROUPS } from '../../constants/ageGroups';
import { getAvailableTimezones, getTimezoneAbbreviation, getTimezoneOptionLabel } from '../../utils/timezoneUtils';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Pagination,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TextField,
  Stack,
  Tooltip
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  Visibility,
  Refresh,
  Group,
  Person,
  PersonAdd,
  Schedule,
  DoneAll,
  PersonSearch
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import BatchCreationForm from '../../components/BatchCreationForm';
import FindFreeTeachersDialog from '../../components/FindFreeTeachersDialog';
import BatchPausePanel from '../../components/BatchPausePanel';
import BatchAssignedStudentsPanel from '../../components/BatchAssignedStudentsPanel';
import BatchStudentsHoverPopover, {
  useBatchStudentsHover,
} from '../../components/BatchStudentsHoverPopover';
import BulkScheduleShiftDialog from '../../components/BulkScheduleShiftDialog';
import { LabelWithInfo } from '../../components/InfoTooltip';
import { batchesAPI } from '../../services/batches';
import { allocationsAPI } from '../../services/api';
import { getCountries } from '../../data/locationData';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { unwrapBatchDetail, getBatchStudents } from '../../utils/batchDetail';
import {
  buildSchedulesFromTimeFilters,
  filtersToPrefillSchedules,
  formatTimeSearchLabel,
  hasCompleteTimeSearch,
  hasPartialTimeSearch,
  windowDurationMinutes,
} from '../../utils/batchSearchSchedules';
import { prefillSchedulesFromTeacher } from '../../utils/teacherWindowFit';

const dayOptions = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 }
];

const ageGroups = AGE_GROUPS;
const batchAgeOptions = [
  { value: '0-1', label: '0–1 months' },
  { value: '1-2', label: '1–2 months' },
  { value: '2-3', label: '2–3 months' },
  { value: '3-4', label: '3–4 months' },
  { value: '4-5', label: '4–5 months' },
  { value: '5-6', label: '5–6 months' },
  { value: '6+', label: '6+ months' },
];

function formatBatchAgeMonths(months) {
  if (months == null || Number.isNaN(Number(months))) return '';
  const n = Number(months);
  if (n <= 0) return 'Less than 1 month old';
  if (n === 1) return '1 month old';
  return `${n} months old`;
}

function formatTimezoneStudentMatch(batch) {
  const matched = Number(batch?.timezone_student_matches);
  const total = Number(batch?.student_count);
  if (!Number.isFinite(matched) || !Number.isFinite(total) || total <= 0) return '';
  if (matched >= total) return total === 1 ? 'All 1 student' : `All ${total} students`;
  return `${matched} of ${total} students`;
}
const countryOptions = getCountries();
const initialFilters = {
  search: '',
  teacher_country: '',
  student_country: '',
  student_timezone: '',
  batch_timezone: '',
  age_group: '',
  batch_age: '',
  days: [],
  time_from: '',
  time_to: '',
  teacher_id: '',
  batch_type: '',
  status: '',
  is_inclusive: '',
  has_capacity: '',
  sort_by: 'created_at',
  sort_dir: 'desc',
};

const Batches = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { canManageBatches, user } = useAuth();
  const showTeacherAck = canManageBatches();
  const canBulkShift = showTeacherAck;
  const [batchCreationOpen, setBatchCreationOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [findTeachersOpen, setFindTeachersOpen] = useState(false);
  const [bulkShiftOpen, setBulkShiftOpen] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState({});
  const [selectingAllFiltered, setSelectingAllFiltered] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignStudentDialogOpen, setAssignStudentDialogOpen] = useState(false);
  const [selectedBatchForAssignment, setSelectedBatchForAssignment] = useState(null);
  const [unassignedStudents, setUnassignedStudents] = useState([]);
  const [loadingUnassignedStudents, setLoadingUnassignedStudents] = useState(false);
  const [assigningStudent, setAssigningStudent] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [assignStudentSearch, setAssignStudentSearch] = useState('');
  const [assignWithoutTimePreference, setAssignWithoutTimePreference] = useState(false);
  const [assignError, setAssignError] = useState(null); // { studentId, message, validation }
  const [unassigningAssignmentId, setUnassigningAssignmentId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const statusFromUrl = params.get('status');
    return {
      ...initialFilters,
      ...(statusFromUrl ? { status: statusFromUrl } : {}),
    };
  });

  // Apply ?status= from deep links (e.g. dashboard Live batches → /batches?status=live)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const statusFromUrl = params.get('status');
    if (statusFromUrl == null) return;
    setFilters((prev) => (prev.status === statusFromUrl ? prev : { ...prev, status: statusFromUrl }));
    setPage(1);
  }, [location.search]);

  const syncStatusInUrl = (statusValue) => {
    const params = new URLSearchParams(location.search || '');
    if (statusValue) params.set('status', statusValue);
    else params.delete('status');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
    if (field === 'status') syncStatusInUrl(value);
  };

  const handleClearFilters = () => {
    setFilters({ ...initialFilters });
    setPage(1);
    syncStatusInUrl('');
  };

  const studentsHover = useBatchStudentsHover();

  // Build teacher filter params (exclude teacher_id to avoid circular dependency)
  const teacherFilterParams = useMemo(() => ({
    search: filters.search || undefined,
    teacher_country: filters.teacher_country || undefined,
    student_country: filters.student_country || undefined,
    student_timezone: filters.student_timezone || undefined,
    batch_timezone: filters.batch_timezone || undefined,
    age_group: filters.age_group || undefined,
    batch_age: filters.batch_age || undefined,
    days: filters.days.length ? filters.days.join(',') : undefined,
    time_from: filters.time_from || undefined,
    time_to: filters.time_to || undefined,
    batch_type: filters.batch_type || undefined,
    status: filters.status || undefined,
    is_inclusive: filters.is_inclusive !== '' ? filters.is_inclusive : undefined,
    has_capacity: filters.has_capacity !== '' ? filters.has_capacity : undefined,
    sort_by: filters.sort_by || undefined,
    sort_dir: filters.sort_dir || undefined,
  }), [filters]);

  const { data: teachersResponse } = useQuery(
    ['teachers-with-batch-counts', teacherFilterParams],
    () => batchesAPI.getTeachersWithCounts(teacherFilterParams),
    {
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  );

  const teachers = useMemo(
    () => teachersResponse?.data?.data || [],
    [teachersResponse?.data?.data]
  );

  const apiParams = useMemo(() => ({
    page,
    limit,
    search: filters.search || undefined,
    teacher_country: filters.teacher_country || undefined,
    student_country: filters.student_country || undefined,
    student_timezone: filters.student_timezone || undefined,
    batch_timezone: filters.batch_timezone || undefined,
    age_group: filters.age_group || undefined,
    batch_age: filters.batch_age || undefined,
    days: filters.days.length ? filters.days.join(',') : undefined,
    time_from: filters.time_from || undefined,
    time_to: filters.time_to || undefined,
    teacher_id: filters.teacher_id || undefined,
    batch_type: filters.batch_type || undefined,
    status: filters.status || undefined,
    is_inclusive: filters.is_inclusive !== '' ? filters.is_inclusive : undefined,
    has_capacity: filters.has_capacity !== '' ? filters.has_capacity : undefined,
    sort_by: filters.sort_by || undefined,
    sort_dir: filters.sort_dir || undefined,
  }), [page, limit, filters]);

  const getTeacherLabel = (teacher) => {
    const name = teacher.name || teacher.full_name || [teacher.first_name, teacher.last_name].filter(Boolean).join(' ').trim();
    return name || teacher.email || teacher.id;
  };

  // Fetch batches with React Query
  const { 
    data: batchesResponse, 
    isLoading, 
    error, 
    refetch 
  } = useQuery(
    ['batches', apiParams],
    () => batchesAPI.getAll(apiParams),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
    }
  );

  const batches = batchesResponse?.data?.data || [];
  const pagination = batchesResponse?.data?.pagination || { page: 1, limit: 20, total: 0, pages: 1 };

  const selectedBatchList = useMemo(() => Object.values(selectedBatches), [selectedBatches]);
  const selectedCount = selectedBatchList.length;
  const pageSelectedCount = batches.filter((b) => selectedBatches[b.id]).length;
  const allPageSelected = batches.length > 0 && pageSelectedCount === batches.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  const batchSummary = (batch) => ({
    id: batch.id,
    name: batch.name,
    teacher_country: batch.teacher_country,
    teacher_timezone: batch.teacher_timezone,
  });

  const toggleBatchSelection = (batch) => {
    setSelectedBatches((prev) => {
      const next = { ...prev };
      if (next[batch.id]) {
        delete next[batch.id];
      } else {
        next[batch.id] = batchSummary(batch);
      }
      return next;
    });
  };

  const selectAllOnPage = () => {
    setSelectedBatches((prev) => {
      const next = { ...prev };
      batches.forEach((b) => {
        next[b.id] = batchSummary(b);
      });
      return next;
    });
  };

  const clearPageSelection = () => {
    setSelectedBatches((prev) => {
      const next = { ...prev };
      batches.forEach((b) => {
        delete next[b.id];
      });
      return next;
    });
  };

  const clearAllSelection = () => setSelectedBatches({});

  const removeFromSelection = (batchId) => {
    setSelectedBatches((prev) => {
      const next = { ...prev };
      delete next[batchId];
      return next;
    });
  };

  const selectAllMatchingFilters = async () => {
    setSelectingAllFiltered(true);
    try {
      const res = await batchesAPI.getAll({ ...apiParams, page: 1, limit: 100 });
      const list = res.data?.data || [];
      const total = res.data?.pagination?.total ?? list.length;
      if (!list.length) {
        toast.error('No batches match the current filters');
        return;
      }
      setSelectedBatches((prev) => {
        const next = { ...prev };
        list.forEach((b) => {
          next[b.id] = batchSummary(b);
        });
        return next;
      });
      if (total > 100) {
        toast(`Selected first 100 of ${total} matching batches`, { icon: 'ℹ️' });
      } else {
        toast.success(`Selected ${list.length} batch(es)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load matching batches');
    } finally {
      setSelectingAllFiltered(false);
    }
  };

  const activeFilterSummary = useMemo(() => {
    const parts = [];
    if (filters.search) parts.push(`Name: ${filters.search}`);
    if (filters.teacher_country) parts.push(`Teacher country: ${filters.teacher_country}`);
    if (filters.student_country) parts.push(`Student country: ${filters.student_country}`);
    if (filters.student_timezone) parts.push(`Student timezone: ${filters.student_timezone}`);
    if (filters.batch_timezone) parts.push(`Batch timezone: ${getTimezoneOptionLabel(filters.batch_timezone)}`);
    if (filters.age_group) parts.push(`Age group: ${filters.age_group}`);
    if (filters.batch_age) {
      const ageOpt = batchAgeOptions.find((o) => o.value === filters.batch_age);
      parts.push(`Batch age: ${ageOpt?.label || filters.batch_age}`);
    }
    if (filters.days.length) {
      parts.push(`Days: ${filters.days.map((val) => dayOptions.find((d) => d.value === val)?.label || val).join(', ')}`);
    }
    if (filters.time_from && filters.time_to) parts.push(`Time: ${filters.time_from}–${filters.time_to}`);
    if (filters.teacher_id) {
      const t = teachers.find((x) => x.id === filters.teacher_id);
      if (t) parts.push(`Teacher: ${getTeacherLabel(t)}`);
    }
    if (filters.status) parts.push(`Status: ${filters.status}`);
    if (filters.batch_type) parts.push(`Type: ${filters.batch_type}`);
    return parts;
  }, [filters, teachers]);

  const showMatchColumn = filters.days.length > 0 || Boolean(filters.time_from && filters.time_to);
  const canFindFreeTeachers = canManageBatches();
  const completeTimeSearch = hasCompleteTimeSearch(filters);
  const partialTimeSearch = hasPartialTimeSearch(filters);
  const findTeacherSchedules = useMemo(() => buildSchedulesFromTimeFilters(filters), [filters]);
  const timeSearchLabel = useMemo(() => formatTimeSearchLabel(filters, dayOptions), [filters]);
  const tableColSpan = (showTeacherAck ? 9 : 8) + (canBulkShift ? 1 : 0) + (showMatchColumn ? 1 : 0);

  // Support deep-link: /batches?create=1 opens the create dialog (Create Class).
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('create') !== '1') return;
    setCreatePrefill(null);
    setBatchCreationOpen(true);
    params.delete('create');
    navigate(
      { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  // Support deep-link: /batches?view=<batchId> opens the View dialog
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const viewId = params.get('view');
    if (!viewId) return;
    // If already open for same batch, do nothing
    if (viewDialogOpen && selectedBatch?.id === viewId) return;
    setSelectedBatch({ id: viewId, name: 'Batch' });
    setViewDialogOpen(true);
    // Clean query param to avoid re-opening on rerender/back
    params.delete('view');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate, selectedBatch?.id, viewDialogOpen]);

  // Fetch full batch details (including students) when viewing
  const { 
    data: batchDetailsResponse, 
    isLoading: isLoadingBatchDetails
  } = useQuery(
    ['batch', selectedBatch?.id],
    () => batchesAPI.getById(selectedBatch.id),
    {
      enabled: !!selectedBatch && (viewDialogOpen || editDialogOpen),
      refetchOnWindowFocus: false,
      refetchOnMount: 'always', // Always refetch when dialog opens
      staleTime: 0, // Always consider data stale to force refetch
    }
  );

  const batchDetails = unwrapBatchDetail(batchDetailsResponse);
  const batchStudents = getBatchStudents(batchDetails);

  // When opened via deep-link, hydrate selectedBatch so the View dialog doesn't rely on placeholder fields.
  useEffect(() => {
    if (!batchDetails || !viewDialogOpen) return;
    if (!selectedBatch?.id || String(selectedBatch.id) !== String(batchDetails.id)) return;
    // Merge in any missing fields used by the view dialog (status, dates, capacity, etc.)
    setSelectedBatch((prev) => ({ ...prev, ...batchDetails }));
  }, [batchDetails, selectedBatch?.id, viewDialogOpen]);

  // Handle assign student
  const handleAssignStudent = (batch) => {
    setSelectedBatchForAssignment(batch);
    setAssignStudentDialogOpen(true);
    setAssignStudentSearch('');
    setAssignWithoutTimePreference(false);
    setAssignError(null);
    fetchUnassignedStudents(batch, '');
  };

  const fetchUnassignedStudents = async (batch, search = '') => {
    if (!batch) return;
    setLoadingUnassignedStudents(true);
    try {
      const params = { limit: 100, batchId: batch.id };
      if (batch.age_group) params.age_group = batch.age_group;
      if (search && String(search).trim()) params.search = String(search).trim();
      const response = await allocationsAPI.getUnassignedStudents(params);
      setUnassignedStudents(response.data?.students || response.data?.data || []);
    } catch (error) {
      console.error('Error fetching unassigned students:', error);
      toast.error('Failed to load unassigned students');
      setUnassignedStudents([]);
    } finally {
      setLoadingUnassignedStudents(false);
    }
  };

  const handleAssignStudentSearchApply = () => {
    if (selectedBatchForAssignment) {
      fetchUnassignedStudents(selectedBatchForAssignment, assignStudentSearch);
    }
  };

  const handleUnassignFromBatch = async (assignmentId, payload) => {
    if (!assignmentId || !selectedBatch?.id) return;
    setUnassigningAssignmentId(assignmentId);
    try {
      const response = await allocationsAPI.unassignOnLeave(assignmentId, payload || {});
      toast.success(response?.data?.message || 'Student unassigned');
      queryClient.invalidateQueries(['batch', selectedBatch.id]);
      queryClient.invalidateQueries(['batches']);
    } catch (error) {
      console.error('Unassign error:', error);
      toast.error(error.response?.data?.message || 'Failed to unassign student');
      throw error;
    } finally {
      setUnassigningAssignmentId(null);
    }
  };

  const handleAssignStudentToBatch = async (studentId, forceAssign = false) => {
    if (!selectedBatchForAssignment) return;
    setAssignError(null);
    setAssigningStudent(true);
    setSelectedStudentId(studentId);

    const doForceAssign = forceAssign || assignWithoutTimePreference;

    try {
      await allocationsAPI.assignStudent({
        studentId,
        batchId: selectedBatchForAssignment.id,
        assignmentType: 'manual',
        adminNotes: `Assigned manually from batch list`,
        forceAssign: doForceAssign
      });

      toast.success('Student assigned successfully!');
      setAssignStudentDialogOpen(false);
      setSelectedBatchForAssignment(null);
      setSelectedStudentId(null);
      setAssignError(null);

      queryClient.invalidateQueries(['batches']);
      if (selectedBatch && selectedBatch.id === selectedBatchForAssignment.id) {
        queryClient.invalidateQueries(['batch', selectedBatch.id]);
      }
    } catch (error) {
      console.error('Error assigning student:', error);
      const data = error.response?.data;
      const status = error.response?.status;

      if (status === 400 && data?.validation) {
        const v = data.validation;
        const parts = [];
        if (v.warnings && v.warnings.length) {
          parts.push(...v.warnings);
        }
        if (v.validationResults && typeof v.validationResults === 'object') {
          Object.entries(v.validationResults).forEach(([key, result]) => {
            if (result && !result.passed && result.details) {
              parts.push(`${key}: ${result.details}`);
            }
          });
        }
        const message = parts.length ? parts.join('; ') : (data?.message || 'Assignment validation failed');
        setAssignError({ studentId, message, validation: v });
        toast.error(message);
      } else {
        const errorMessage = data?.message || error.message || 'Failed to assign student';
        toast.error(errorMessage);
      }
    } finally {
      setAssigningStudent(false);
      setSelectedStudentId(null);
    }
  };

  const handleBatchCreated = () => {
    refetch();
    setBatchCreationOpen(false);
    setCreatePrefill(null);
  };

  const openBlankCreate = () => {
    setCreatePrefill(null);
    setBatchCreationOpen(true);
  };

  const openCreateFromTeacherSearch = (teacher) => {
    setFindTeachersOpen(false);
    const windowSchedules = filtersToPrefillSchedules(filters);
    setCreatePrefill({
      teacher_id: teacher?.id ? String(teacher.id) : '',
      teacher_name: teacher?.name || '',
      teacher_email: teacher?.email || '',
      teacher_timezone: teacher?.timezone || '',
      age_group: filters.age_group || '',
      batch_type: filters.batch_type || 'group',
      max_capacity: filters.batch_type === 'one_on_one' ? 1 : 10,
      schedules: teacher
        ? prefillSchedulesFromTeacher(teacher, filters.days, windowSchedules)
        : windowSchedules,
    });
    setBatchCreationOpen(true);
  };

  const handleViewBatch = (batch) => {
    // Open batch details as a full page (teacher UX)
    navigate(`/batches/${batch.id}`);
  };

  const handleEditBatch = async (batch) => {
    setSelectedBatch(batch);
    setEditDialogOpen(true);
    // Invalidate and refetch batch details to ensure fresh data
    await queryClient.invalidateQueries(['batch', batch.id]);
    // Force a refetch to get the latest data
    queryClient.refetchQueries(['batch', batch.id]);
  };

  const handleBatchUpdated = () => {
    // Invalidate batch details cache to ensure fresh data on next open
    if (selectedBatch?.id) {
      queryClient.invalidateQueries(['batch', selectedBatch.id]);
    }
    // Refresh the batches list
    refetch();
    setEditDialogOpen(false);
    setSelectedBatch(null);
  };

  const handleDeleteBatch = async (batch) => {
    if (window.confirm(`Are you sure you want to delete batch "${batch.name}"?`)) {
      try {
        await batchesAPI.delete(batch.id);
        await queryClient.invalidateQueries({ queryKey: ['batches'] });
        await queryClient.invalidateQueries({ queryKey: ['teachers-with-batch-counts'] });
        await queryClient.invalidateQueries({ queryKey: ['batch', batch.id] });
        await refetch();
        alert('Batch deactivated successfully. The teacher time slot is now available for other batches or demos.');
      } catch (error) {
        alert('Failed to deactivate batch: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'inactive':
        return 'default';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      case 'to_start':
        return 'warning';
      case 'in_progress':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return formatCalendarDate(dateString);
  };

  // --- Timezone helpers for schedule display ---
  const VIEWER_TZ = getViewerTimezone(user?.timezone);
  const VIEWER_TZ_ABBR = getTimezoneAbbreviation(VIEWER_TZ);
  const IST_TZ = 'Asia/Kolkata';
  const VIEWER_TZ_LABEL = (() => {
    // Prefer the user's requested display like "US/Newyork"
    if (VIEWER_TZ.startsWith('America/')) {
      const cityRaw = VIEWER_TZ.split('/')[1] || '';
      const city = cityRaw.replace(/_/g, '').toLowerCase();
      const cityPretty = city ? city.charAt(0).toUpperCase() + city.slice(1) : cityRaw;
      return `US/${cityPretty}`;
    }
    return VIEWER_TZ;
  })();

  const getTzOffsetMinutes = (tz, utcDate) => {
    // Returns offset minutes for tz at the instant utcDate.
    // Technique: format the UTC instant in tz, then interpret that wall time as UTC and diff.
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const parts = dtf.formatToParts(utcDate);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const y = Number(get('year'));
    const m = Number(get('month'));
    const d = Number(get('day'));
    const hh = Number(get('hour'));
    const mm = Number(get('minute'));
    const ss = Number(get('second'));
    const asUTC = Date.UTC(y, m - 1, d, hh, mm, ss);
    return (asUTC - utcDate.getTime()) / 60000;
  };

  const zonedDateTimeToUtcDate = (dateStrYYYYMMDD, timeStrHHMMSS, timeZone) => {
    if (!dateStrYYYYMMDD || !timeStrHHMMSS || !timeZone) return null;
    const [y, m, d] = String(dateStrYYYYMMDD).slice(0, 10).split('-').map(Number);
    const [hh, mm, ss] = String(timeStrHHMMSS).slice(0, 8).split(':').map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
    // Initial guess: interpret wall time as UTC, then subtract tz offset.
    const guessUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
    const guessDate = new Date(guessUtcMs);
    const offsetMin = getTzOffsetMinutes(timeZone, guessDate);
    return new Date(guessUtcMs - offsetMin * 60000);
  };

  const formatTimeRangeInTz = (utcDateStart, utcDateEnd, timeZone) => {
    if (!utcDateStart || !utcDateEnd) return '—';
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${fmt.format(utcDateStart)} - ${fmt.format(utcDateEnd)}`;
  };

  const getBatchTypeIcon = (batchType) => {
    return batchType === 'group' ? <Group fontSize="small" /> : <Person fontSize="small" />;
  };

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading batches: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" gutterBottom>
            Batches
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              Refresh
            </Button>
            {canBulkShift && (
              <Button
                variant={selectedCount > 0 ? 'contained' : 'outlined'}
                startIcon={<Schedule />}
                onClick={() => {
                  if (selectedCount === 0) {
                    toast('Select batches using the checkboxes, or use "Select all matching filters"', { icon: 'ℹ️' });
                    return;
                  }
                  setBulkShiftOpen(true);
                }}
              >
                Shift schedules{selectedCount > 0 ? ` (${selectedCount})` : ''}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openBlankCreate}
            >
              Create New Batch
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search batch"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by batch name"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Teacher Country</InputLabel>
                <Select
                  label="Teacher Country"
                  value={filters.teacher_country}
                  onChange={(e) => handleFilterChange('teacher_country', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {countryOptions.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Student Country</InputLabel>
                <Select
                  label="Student Country"
                  value={filters.student_country}
                  onChange={(e) => handleFilterChange('student_country', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {countryOptions.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Student Timezone</InputLabel>
                <Select
                  label="Student Timezone"
                  value={filters.student_timezone}
                  onChange={(e) => handleFilterChange('student_timezone', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {getAvailableTimezones().map((timezone) => (
                    <MenuItem key={timezone.value} value={timezone.value}>{timezone.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Batch Timezone</InputLabel>
                <Select
                  label="Batch Timezone"
                  value={filters.batch_timezone}
                  onChange={(e) => handleFilterChange('batch_timezone', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {getAvailableTimezones().map((timezone) => (
                    <MenuItem key={timezone.value} value={timezone.value}>{timezone.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Age Group</InputLabel>
                <Select
                  label="Age Group"
                  value={filters.age_group}
                  onChange={(e) => handleFilterChange('age_group', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {ageGroups.map(group => (
                    <MenuItem key={group} value={group}>{group}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Batch age</InputLabel>
                <Select
                  label="Batch age"
                  value={filters.batch_age}
                  onChange={(e) => handleFilterChange('batch_age', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {batchAgeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Days</InputLabel>
                <Select
                  multiple
                  label="Days"
                  value={filters.days}
                  onChange={(e) => handleFilterChange('days', e.target.value)}
                  input={<OutlinedInput label="Days" />}
                  renderValue={(selected) => selected.map((val) => dayOptions.find(d => d.value === val)?.label || val).join(', ')}
                >
                  {dayOptions.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      <Checkbox checked={filters.days.indexOf(day.value) > -1} />
                      <ListItemText primary={day.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Teacher</InputLabel>
                <Select
                  label="Teacher"
                  value={filters.teacher_id}
                  onChange={(e) => handleFilterChange('teacher_id', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <span>{getTeacherLabel(teacher)}</span>
                        <Chip 
                          label={`${teacher.batch_count || 0} batches`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Start Time (teacher tz)"
                type="time"
                value={filters.time_from}
                onChange={(e) => handleFilterChange('time_from', e.target.value)}
                inputProps={{ step: 300 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="End Time (teacher tz)"
                type="time"
                value={filters.time_to}
                onChange={(e) => handleFilterChange('time_to', e.target.value)}
                inputProps={{ step: 300 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Has Capacity</InputLabel>
                <Select
                  label="Has Capacity"
                  value={filters.has_capacity}
                  onChange={(e) => handleFilterChange('has_capacity', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  <MenuItem value="true">Available seats</MenuItem>
                  <MenuItem value="false">Full</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  <MenuItem value="live">Live (to start / in progress)</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="to_start">To start</MenuItem>
                  <MenuItem value="in_progress">In progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="all">All (incl. inactive)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort</InputLabel>
                <Select
                  label="Sort"
                  value={`${filters.sort_by}:${filters.sort_dir}`}
                  onChange={(e) => {
                    const [by, dir] = String(e.target.value || '').split(':');
                    handleFilterChange('sort_by', by || 'created_at');
                    handleFilterChange('sort_dir', dir || 'desc');
                  }}
                >
                  <MenuItem value="created_at:desc">Newest</MenuItem>
                  <MenuItem value="created_at:asc">Oldest</MenuItem>
                  <MenuItem value="name:asc">Name (A → Z)</MenuItem>
                  <MenuItem value="name:desc">Name (Z → A)</MenuItem>
                  <MenuItem value="start_date:asc">Start date (earliest)</MenuItem>
                  <MenuItem value="start_date:desc">Start date (latest)</MenuItem>
                  <MenuItem value="status:asc">Status (Paused first)</MenuItem>
                  <MenuItem value="status:desc">Status (Paused last)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleClearFilters}>
                  Clear
                </Button>
                <Button variant="contained" onClick={() => refetch()}>
                  Apply
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Age group is an exact filter. Days and time return overlapping batches, labeled Exact
            (same days set / identical start and end) or Partial (missing or extra days, or overlapping times).
            Batch age is whole months since the batch start date (future starts count as 0–1).
          </Typography>
        </Paper>

        {canFindFreeTeachers && completeTimeSearch && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  None of these work?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {windowDurationMinutes(filters.time_from, filters.time_to) >= 60
                    ? `Find a teacher with a free 1-hour class inside ${timeSearchLabel}, then start a new class.`
                    : `Find a teacher who is free at ${timeSearchLabel}, then start a new class.`}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<PersonSearch />}
                onClick={() => setFindTeachersOpen(true)}
              >
                Find a free teacher
              </Button>
            </Box>
          </Paper>
        )}
        {canFindFreeTeachers && partialTimeSearch && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Find a free teacher
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose days and a start and end time above, then you can see who is free.
            </Typography>
          </Paper>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Matching filters
                </Typography>
                <Typography variant="h4">
                  {pagination.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Live Batches
                </Typography>
                <Typography variant="h4">
                  {filters.status === 'live'
                    ? (pagination.total || 0)
                    : batches.filter((b) => ['active', 'to_start', 'in_progress', 'paused'].includes(b.status)).length}
                </Typography>
                {filters.status !== 'live' && (
                  <Typography variant="caption" color="text.secondary">
                    On this page
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Group Batches
                </Typography>
                <Typography variant="h4">
                  {batches.filter(b => b.batch_type === 'group').length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  On this page
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  One-on-One Batches
                </Typography>
                <Typography variant="h4">
                  {batches.filter(b => b.batch_type === 'one_on_one').length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  On this page
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Bulk selection toolbar */}
        {canBulkShift && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: selectedCount > 0 ? 'action.selected' : 'background.paper' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" fontWeight="medium" sx={{ mr: 1 }}>
                Bulk schedule shift
              </Typography>
              <Tooltip title="Select every batch on this page (keeps selections from other pages)">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DoneAll />}
                  onClick={selectAllOnPage}
                  disabled={!batches.length}
                >
                  This page ({batches.length})
                </Button>
              </Tooltip>
              <Tooltip title="Select up to 100 batches matching current filters — use for DST by country">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selectAllMatchingFilters}
                    disabled={selectingAllFiltered || pagination.total === 0}
                  >
                    {selectingAllFiltered ? (
                      <CircularProgress size={18} />
                    ) : (
                      `All matching filters (${Math.min(pagination.total, 100)}${pagination.total > 100 ? '+' : ''})`
                    )}
                  </Button>
                </span>
              </Tooltip>
              {pageSelectedCount > 0 && (
                <Button size="small" onClick={clearPageSelection}>
                  Clear this page
                </Button>
              )}
              {selectedCount > 0 && (
                <>
                  <Chip
                    label={`${selectedCount} selected`}
                    color="primary"
                    size="small"
                    onDelete={clearAllSelection}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Schedule />}
                    onClick={() => setBulkShiftOpen(true)}
                  >
                    Continue to shift
                  </Button>
                </>
              )}
            </Stack>
            {activeFilterSummary.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Active filters: {activeFilterSummary.join(' · ')}
              </Typography>
            )}
            {selectedCount > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Selection persists across pages. Max 100 batches per shift run.
              </Typography>
            )}
          </Paper>
        )}

        {/* Pagination Info at Top */}
        {pagination.total > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} batches
            </Typography>
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={(event, newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Batches Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {canBulkShift && (
                    <TableCell padding="checkbox">
                      <Tooltip title="Select all on this page">
                        <Checkbox
                          indeterminate={somePageSelected}
                          checked={allPageSelected}
                          onChange={(e) => {
                            if (e.target.checked) selectAllOnPage();
                            else clearPageSelection();
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell><strong>Batch Name</strong></TableCell>
                  {showMatchColumn && <TableCell><strong>Match</strong></TableCell>}
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Age Group</strong></TableCell>
                  <TableCell><strong>Capacity</strong></TableCell>
                  <TableCell><strong>Teacher</strong></TableCell>
                  {showTeacherAck && <TableCell><strong>Teacher saw batch</strong></TableCell>}
                  <TableCell><strong>Start Date</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : batches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColSpan} align="center">
                      <Typography variant="body1" color="textSecondary">
                        {completeTimeSearch
                          ? 'No class at this time. Find a free teacher above, or create a new batch.'
                          : 'No batches found. Create your first batch!'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  batches.map((batch) => (
                    <TableRow
                      key={batch.id}
                      hover
                      selected={!!selectedBatches[batch.id]}
                      sx={canBulkShift ? { cursor: 'pointer' } : undefined}
                      onClick={canBulkShift ? () => toggleBatchSelection(batch) : undefined}
                    >
                      {canBulkShift && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={!!selectedBatches[batch.id]}
                            onChange={() => toggleBatchSelection(batch)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getBatchTypeIcon(batch.batch_type)}
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ cursor: 'default', textDecoration: 'underline dotted' }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => studentsHover.open(e.currentTarget, batch)}
                            onMouseLeave={studentsHover.onTriggerLeave}
                          >
                            {batch.name}
                          </Typography>
                          {batch.is_inclusive && (
                            <Chip 
                              label="Inclusive" 
                              size="small" 
                              color="secondary" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {(batch.country || batch.timezone) && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {[batch.country, batch.timezone ? getTimezoneOptionLabel(batch.timezone) : '']
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        )}
                        {filters.student_timezone && formatTimezoneStudentMatch(batch) ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {formatTimezoneStudentMatch(batch)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      {showMatchColumn && (
                        <TableCell>
                          {batch.match_type === 'exact' && (
                            <Chip label="Exact match" size="small" color="success" />
                          )}
                          {batch.match_type === 'partial' && (
                            <Tooltip
                              title={(batch.match_details?.reasons || []).join(' · ') || 'Partial match'}
                            >
                              <Box>
                                <Chip label="Partial match" size="small" color="warning" />
                                {(batch.match_details?.reasons || []).length > 0 && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {batch.match_details.reasons.join(' · ')}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          )}
                          {!batch.match_type && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip 
                          label={batch.batch_type === 'group' ? 'Group' : 'One-on-One'} 
                          size="small" 
                          color={batch.batch_type === 'group' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={batch.age_group} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {batch.current_capacity}/{batch.max_capacity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {batch.teacher_name || 'Not assigned'}
                        </Typography>
                        {(batch.teacher_country || batch.teacher_timezone) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {batch.teacher_country || 'N/A'}{batch.teacher_timezone ? ` · ${batch.teacher_timezone}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      {showTeacherAck && (
                        <TableCell>
                          {batch.teacherAssignmentAck?.status === 'acknowledged' && (
                            <Chip size="small" color="success" label="Acknowledged" />
                          )}
                          {batch.teacherAssignmentAck?.status === 'pending' && (
                            <Chip size="small" color="warning" label="Not yet seen" />
                          )}
                          {(!batch.teacherAssignmentAck || batch.teacherAssignmentAck.status === 'not_applicable') && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(batch.start_date)}
                        </Typography>
                        {batch.batch_age_months != null && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatBatchAgeMonths(batch.batch_age_months)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={batch.status.replace('_', ' ')} 
                          size="small" 
                          color={getStatusColor(batch.status)}
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={1}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewBatch(batch)}
                            title="View Batch Details"
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleAssignStudent(batch)}
                            title="Assign Student"
                            disabled={batch.current_capacity >= batch.max_capacity}
                          >
                            <PersonAdd />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleEditBatch(batch)}
                            title="Edit Batch"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteBatch(batch)}
                            title="Delete Batch"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <BatchStudentsHoverPopover hover={studentsHover} />

          {/* Pagination */}
          {pagination.total > 0 && (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 3, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} batches
                </Typography>
                <Pagination
                  count={pagination.pages}
                  page={pagination.page}
                  onChange={(event, newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Standalone Batch Creation Form */}
      <BatchCreationForm
        key={createPrefill ? `create-prefill-${createPrefill.teacher_id || 'none'}` : 'create-blank'}
        open={batchCreationOpen}
        onClose={() => {
          setBatchCreationOpen(false);
          setCreatePrefill(null);
        }}
        onSuccess={handleBatchCreated}
        prefill={createPrefill}
      />

      <FindFreeTeachersDialog
        open={findTeachersOpen}
        onClose={() => setFindTeachersOpen(false)}
        schedules={findTeacherSchedules}
        timeLabel={timeSearchLabel}
        onCreateWithTeacher={openCreateFromTeacherSearch}
      />

      {/* View Batch Dialog */}
      {selectedBatch && (
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Group />
              View Batch - {selectedBatch.name}
            </Box>
          </DialogTitle>
          <DialogContent>
            {isLoadingBatchDetails ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Batch Name</Typography>
                    <Typography variant="body1">{selectedBatch.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Country</Typography>
                    <Typography variant="body1">{batchDetails?.country || selectedBatch.country || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Timezone</Typography>
                    <Typography variant="body1">
                      {(batchDetails?.timezone || selectedBatch.timezone)
                        ? getTimezoneOptionLabel(batchDetails?.timezone || selectedBatch.timezone)
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                    <Chip 
                      label={selectedBatch.batch_type === 'group' ? 'Group' : 'One-on-One'} 
                      size="small" 
                      color={selectedBatch.batch_type === 'group' ? 'primary' : 'secondary'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Age Group</Typography>
                    <Typography variant="body1">{selectedBatch.age_group}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Capacity</Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body1">
                        {batchDetails?.actual_student_count !== undefined 
                          ? `${batchDetails.actual_student_count}` 
                          : batchStudents.length}/{selectedBatch.max_capacity}
                      </Typography>
                      {batchDetails && batchDetails.actual_student_count !== undefined && batchDetails.actual_student_count !== selectedBatch.current_capacity && (
                        <Chip 
                          label={`Stored: ${selectedBatch.current_capacity}`} 
                          size="small" 
                          color="warning"
                          variant="outlined"
                          title="Capacity mismatch: stored value differs from actual student count"
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      <LabelWithInfo
                        label="Primary teacher"
                        title="Main teacher"
                        body="Teaches this batch by default."
                      />
                    </Typography>
                    <Typography variant="body1">{batchDetails?.teacher_name || selectedBatch.teacher_name || 'Not assigned'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      <LabelWithInfo
                        label="Secondary teacher"
                        title="Backup teacher"
                        body="Read-only until covering approved leave for this batch."
                      />
                    </Typography>
                    <Typography variant="body1">
                      {batchDetails?.secondary_teacher_name || 'Not assigned'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Start Date</Typography>
                    <Typography variant="body1">{formatDate(selectedBatch.start_date)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                    <Chip 
                      label={String(selectedBatch.status || '—').replace('_', ' ')} 
                      size="small" 
                      color={getStatusColor(selectedBatch.status)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Inclusive</Typography>
                    <Typography variant="body1">{selectedBatch.is_inclusive ? 'Yes' : 'No'}</Typography>
                  </Grid>
                </Grid>

                {/* Batch Schedule/Timing Section */}
                {batchDetails?.schedules && batchDetails.schedules.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>
                      Batch Schedule
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Day</strong></TableCell>
                            <TableCell><strong>Time</strong></TableCell>
                            <TableCell><strong>Duration</strong></TableCell>
                            {batchDetails.schedules[0]?.converted_timezone && (
                              <TableCell><strong>Timezone</strong></TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batchDetails.schedules.map((schedule, index) => {
                            const dayName = dayOptions.find(d => d.value === schedule.day_of_week)?.label || `Day ${schedule.day_of_week}`;
                            const startTimeRaw = schedule.start_time ? schedule.start_time.substring(0, 8) : '';
                            const endTimeRaw = schedule.end_time ? schedule.end_time.substring(0, 8) : '';

                            // Anchor timezone conversion to the batch start_date (handles DST reasonably).
                            const anchorDate = selectedBatch?.start_date || new Date().toISOString().slice(0, 10);
                            const utcStart = zonedDateTimeToUtcDate(anchorDate, startTimeRaw, VIEWER_TZ);
                            const utcEnd = zonedDateTimeToUtcDate(anchorDate, endTimeRaw, VIEWER_TZ);

                            const timeDisplayViewer = startTimeRaw && endTimeRaw
                              ? formatTimeRangeInTz(utcStart, utcEnd, VIEWER_TZ)
                              : 'N/A';
                            const timeDisplayIst = startTimeRaw && endTimeRaw
                              ? formatTimeRangeInTz(utcStart, utcEnd, IST_TZ)
                              : 'N/A';
                            
                            return (
                              <TableRow key={schedule.id || index} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {dayName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {timeDisplayViewer}{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                      ({VIEWER_TZ_LABEL} - {VIEWER_TZ_ABBR})
                                    </Typography>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                    IST {timeDisplayIst}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="textSecondary">
                                    {schedule.duration_minutes || 0} minutes
                                  </Typography>
                                </TableCell>
                                {schedule.converted_timezone && (
                                  <TableCell>
                                    <Typography variant="body2" color="textSecondary" fontSize="0.75rem">
                                      {schedule.converted_timezone}
                                      {schedule.original_timezone && schedule.original_timezone !== schedule.converted_timezone && (
                                        <span> (from {schedule.original_timezone})</span>
                                      )}
                                    </Typography>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* Sessions (auto-created just-in-time) */}
                {batchDetails?.sessions && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>
                      Sessions (recent / next)
                    </Typography>
                    {batchDetails.sessions.length === 0 ? (
                      <Alert severity="info">No sessions created yet for this window.</Alert>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Date</strong></TableCell>
                              <TableCell><strong>Time</strong></TableCell>
                              <TableCell><strong>Kind</strong></TableCell>
                              <TableCell align="right"><strong>Action</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {batchDetails.sessions.slice(0, 10).map((s) => (
                              <TableRow key={s.id} hover>
                                <TableCell>{s.session_date}</TableCell>
                                <TableCell>{String(s.start_time || '').slice(0, 5)} - {String(s.end_time || '').slice(0, 5)}</TableCell>
                                <TableCell>{s.session_kind || 'scheduled'}</TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => window.location.assign(`/attendance/session/${s.id}`)}
                                  >
                                    Mark attendance
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {batchDetails.session_generation?.error === 'jit_failed' ? (
                      <Typography variant="caption" color="error">
                        Session auto-creation failed. Please contact admin.
                      </Typography>
                    ) : null}
                  </>
                )}

                {selectedBatch?.id ? (
                  <BatchPausePanel
                    batchId={selectedBatch.id}
                    activePause={batchDetails?.active_pause}
                  />
                ) : null}

                {/* Students List Section */}
                <Divider sx={{ my: 3 }} />
                <BatchAssignedStudentsPanel
                  batch={batchDetails}
                  students={batchStudents}
                  storedCapacity={selectedBatch.current_capacity}
                  canManageAssignments={canManageBatches()}
                  onUnassign={handleUnassignFromBatch}
                  unassigningAssignmentId={unassigningAssignmentId}
                  loading={isLoadingBatchDetails}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button 
              variant="contained" 
              onClick={() => {
                setViewDialogOpen(false);
                handleEditBatch(selectedBatch);
              }}
            >
              Edit Batch
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Batch Dialog */}
      {selectedBatch && (
        <>
          {isLoadingBatchDetails ? (
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
              <DialogContent>
                <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress />
                </Box>
              </DialogContent>
            </Dialog>
          ) : batchDetails ? (
            <BatchCreationForm
              key={`edit-${selectedBatch.id}-${batchDetails.updated_at || Date.now()}`}
              open={editDialogOpen}
              onClose={() => {
                setEditDialogOpen(false);
                setSelectedBatch(null);
              }}
              onSuccess={handleBatchUpdated}
              mode="edit"
              initialData={batchDetails}
              batchId={selectedBatch.id}
            />
          ) : (
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
              <DialogContent>
                <Alert severity="error">
                  Failed to load batch details. Please try again.
                </Alert>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditDialogOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}

      {/* Assign Student Dialog */}
      <Dialog 
        open={assignStudentDialogOpen} 
        onClose={() => {
          setAssignStudentDialogOpen(false);
          setSelectedBatchForAssignment(null);
          setUnassignedStudents([]);
          setAssignError(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAdd />
            Assign Student to {selectedBatchForAssignment?.name}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Students already in other batches can be added to this batch too.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedBatchForAssignment && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Capacity: {selectedBatchForAssignment.current_capacity}/{selectedBatchForAssignment.max_capacity}
                {selectedBatchForAssignment.age_group && ` • Age Group: ${selectedBatchForAssignment.age_group}`}
              </Alert>
            </Box>
          )}

          {/* Student list search */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Search students"
              placeholder="Name, email, or phone"
              value={assignStudentSearch}
              onChange={(e) => setAssignStudentSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAssignStudentSearchApply()}
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" onClick={handleAssignStudentSearchApply} size="medium">
              Apply
            </Button>
          </Box>

          {/* Assign without checking time preference */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assignWithoutTimePreference}
                  onChange={(e) => setAssignWithoutTimePreference(e.target.checked)}
                />
              }
              label="Assign without checking time preference"
            />
          </Box>

          {assignError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAssignError(null)}>
              <Typography variant="body2" component="span">{assignError.message}</Typography>
            </Alert>
          )}

          {loadingUnassignedStudents ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : unassignedStudents.length === 0 ? (
            <Alert severity="info">
              No students available to assign
              {selectedBatchForAssignment?.age_group ? ` for age group ${selectedBatchForAssignment.age_group}` : ''}.
              All matching students may already be in this batch.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>Age Group</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Other Batches</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unassignedStudents.map((student) => (
                    <TableRow key={student.id || student.student_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {student.name || student.student_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {student.email || student.student_email || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {student.phone || student.student_phone || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={student.age_group || 'N/A'} 
                          size="small" 
                          variant="outlined"
                          color={student.age_group === selectedBatchForAssignment?.age_group ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={student.student_type || student.type || 'normal'} 
                          size="small"
                          color={student.student_type === 'normal' ? 'default' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {student.other_batch_names || (Number(student.active_assignments) > 0 ? `${student.active_assignments} batch(es)` : '—')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} alignItems="center">
                          {assignError?.studentId === (student.id || student.student_id) ? (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleAssignStudentToBatch(student.id || student.student_id, true)}
                                disabled={assigningStudent}
                              >
                                {assigningStudent ? <CircularProgress size={20} /> : 'Assign anyway'}
                              </Button>
                              <Button size="small" onClick={() => setAssignError(null)}>Dismiss</Button>
                            </>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleAssignStudentToBatch(student.id || student.student_id)}
                              disabled={assigningStudent || (selectedStudentId === (student.id || student.student_id))}
                            >
                              {assigningStudent && selectedStudentId === (student.id || student.student_id) ? (
                                <CircularProgress size={20} />
                              ) : (
                                'Assign'
                              )}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAssignStudentDialogOpen(false);
            setSelectedBatchForAssignment(null);
            setUnassignedStudents([]);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <BulkScheduleShiftDialog
        open={bulkShiftOpen}
        onClose={() => setBulkShiftOpen(false)}
        selectedBatches={selectedBatchList}
        onRemoveBatch={removeFromSelection}
        onClearSelection={clearAllSelection}
        onApplied={() => {
          clearAllSelection();
          refetch();
        }}
      />

      {canBulkShift && selectedCount > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.snackbar,
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="xl">
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="body2" fontWeight="medium">
                {selectedCount} batch{selectedCount !== 1 ? 'es' : ''} selected for schedule shift
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={clearAllSelection}>
                  Clear selection
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Schedule />}
                  onClick={() => setBulkShiftOpen(true)}
                >
                  Shift schedules
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Paper>
      )}
    </Container>
  );
};

export default Batches; 